'use client';

import React, { useState } from 'react';

import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      router.push('/');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(160deg, #0B1739 0%, #0f1e47 45%, #1a2a55 100%)' }}>
      {/* Top bar */}
      <div className="flex items-center px-6 h-14 shrink-0">
        <div className="flex items-center gap-2.5">
          <AppLogo size={32} />
          <span className="font-bold text-white text-sm tracking-tight" style={{ letterSpacing: '-0.01em' }}>AdoptionOS</span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          {/* Card */}
          <div className="bg-white rounded-xl border border-white/10 overflow-hidden" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.2)' }}>
            {/* Card header */}
            <div className="px-8 pt-8 pb-6 border-b border-border">
              <div className="flex items-center gap-3 mb-5">
                <AppLogo size={40} />
              </div>
              <h1 className="text-lg font-bold text-navy leading-tight" style={{ letterSpacing: '-0.01em' }}>Sign in to AdoptionOS</h1>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                Access your organization and transformation workspace.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
              {error && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg px-3.5 py-3">
                  <Icon name="ExclamationCircleIcon" size={14} className="text-red-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-700 leading-relaxed">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@organization.com"
                  className="w-full h-9 px-3 text-sm border border-border rounded-md bg-white text-foreground placeholder-muted-foreground focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal transition-colors"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-foreground">Password</label>
                  <button
                    type="button"
                    className="text-xs font-medium hover:underline"
                    style={{ color: '#00A89D' }}
                    onClick={() => setError('Password reset is available via your administrator.')}
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="w-full h-9 px-3 pr-9 text-sm border border-border rounded-md bg-white text-foreground placeholder-muted-foreground focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={14} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-9 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md transition-all flex items-center justify-center gap-2 mt-2"
                style={{ background: 'linear-gradient(90deg, #5667E8, #00A89D)', boxShadow: '0 2px 8px rgba(0,168,157,0.25)' }}
              >
                {loading ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in…
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="px-8 pb-6 pt-0">
              <p className="text-xs text-muted-foreground text-center">
                Contact your administrator to request access.
              </p>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="flex items-center justify-center gap-4 mt-6">
            {['Enterprise Grade', 'SOC 2 Ready', 'GDPR Compliant'].map((item, i) => (
              <div key={i} className="flex items-center gap-1">
                <Icon name="ShieldCheckIcon" size={11} className="text-white/30" />
                <span className="text-2xs text-white/30">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-4 text-center">
        <p className="text-2xs text-white/20">
          © 2026 AdoptionOS · Enterprise Transformation Operating System
        </p>
      </div>
    </div>
  );
}
